
import React, { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { installSafeJsonStringify } from './src/utils/safeJson';

// Install circular-safe JSON serialization globally before any modules initialize
installSafeJsonStringify();

import './src/index.css';
import { motion, AnimatePresence } from 'motion/react';
import { AppProvider, useApp } from './AppContext';
import { Header, Footer } from './Layout';
import { HomePage } from './Home';
import { LeadershipPage } from './Leadership';
import { SpecialistsPage } from './Specialists';
import { ContactPage } from './Contact';
import { AuthPage } from './Auth';
import { Dashboard } from './Dashboard';
import { ConsultationRoom } from './ConsultationRoom';
import { TermsAndServices, PrivacyPolicy, EthicsGuidelines } from './Legal';
import { FAQsPage } from './FAQs';
import { Button, CookieBanner } from './Components';
import { usePwaInstall, InstallPwaModal, forceCacheRefreshAndReload } from './InstallPwa';
import { GlobalLiveCallPopup } from './LiveCallNotificationBanner';

// Global Chunk Load / Cache Invalidation Recovery & Circular Error Shield
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    const msg = event?.message || '';

    // Guard against circular structure error propagating to crash host UI
    if (msg.includes('Converting circular structure to JSON')) {
      console.warn('[ROVITA JSON Guard] Circular structure serialization caught and neutralized.');
      event.preventDefault();
      return;
    }

    if (
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Importing a module script failed') ||
      msg.includes('Loading chunk') ||
      msg.includes('ChunkLoadError')
    ) {
      const hasRetried = sessionStorage.getItem('rovita_chunk_retry');
      if (!hasRetried) {
        sessionStorage.setItem('rovita_chunk_retry', 'true');
        console.warn('[Cache Recovery] Detected stale script chunk error. Purging cache and reloading...');
        forceCacheRefreshAndReload();
      }
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason?.message || event?.reason || '';
    
    // Suppress benign Chrome extension / Service Worker port closure noise
    if (
      typeof reason === 'string' &&
      (reason.includes('A listener indicated an asynchronous response') ||
       reason.includes('message channel closed before a response was received') ||
       reason.includes('The message port closed before a response was received') ||
       reason.includes('Converting circular structure to JSON'))
    ) {
      event.preventDefault();
      return;
    }

    if (
      typeof reason === 'string' &&
      (reason.includes('Failed to fetch dynamically imported module') ||
       reason.includes('Importing a module script failed') ||
       reason.includes('Loading chunk') ||
       reason.includes('ChunkLoadError'))
    ) {
      const hasRetried = sessionStorage.getItem('rovita_chunk_retry');
      if (!hasRetried) {
        sessionStorage.setItem('rovita_chunk_retry', 'true');
        console.warn('[Cache Recovery] Detected chunk unhandled rejection. Purging cache and reloading...');
        forceCacheRefreshAndReload();
      }
    }
  });
}

const LoadingScreen: React.FC = () => (
  <div className="h-screen w-full flex flex-col items-center justify-center bg-white relative overflow-hidden">
    {/* Animated background blobs */}
    <motion.div 
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ 
        opacity: [0.1, 0.2, 0.1],
        scale: [1, 1.2, 1],
        rotate: [0, 90, 0]
      }}
      transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
      className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-indigo-100 rounded-full blur-[100px] -z-10"
    />
    <motion.div 
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ 
        opacity: [0.1, 0.15, 0.1],
        scale: [1.2, 1, 1.2],
        rotate: [0, -90, 0]
      }}
      transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
      className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#008080]/10 rounded-full blur-[100px] -z-10"
    />

    <div className="relative flex flex-col items-center">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="mb-12 relative"
      >
        <div className="w-20 h-20 bg-indigo-600 rounded-[2rem] shadow-2xl shadow-indigo-200 flex items-center justify-center relative overflow-hidden group">
          <motion.div 
            animate={{ 
              rotate: 360 
            }}
            transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
            className="absolute inset-0 bg-gradient-to-tr from-indigo-500 to-indigo-700 opacity-50"
          />
          <i className="fa-solid fa-plus text-white text-3xl relative z-10"></i>
        </div>
        
        {/* Pulsing rings */}
        <motion.div 
          animate={{ scale: [1, 1.5], opacity: [0.5, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
          className="absolute inset-0 border-2 border-indigo-100 rounded-[2.2rem] -z-10"
        />
        <motion.div 
          animate={{ scale: [1, 1.8], opacity: [0.3, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeOut", delay: 0.5 }}
          className="absolute inset-0 border-2 border-indigo-50 rounded-[2.4rem] -z-10"
        />
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.8 }}
        className="text-center"
      >
        <h2 className="text-2xl font-black text-slate-900 tracking-tighter mb-2">ROVITA</h2>
        <div className="flex items-center justify-center gap-3">
          <div className="flex gap-1">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                animate={{ 
                  scale: [1, 1.5, 1],
                  opacity: [0.3, 1, 0.3]
                }}
                transition={{ 
                  duration: 1, 
                  repeat: Infinity, 
                  delay: i * 0.2,
                  ease: "easeInOut"
                }}
                className="w-1.5 h-1.5 bg-indigo-600 rounded-full"
              />
            ))}
          </div>
          <p className="font-black uppercase tracking-[0.3em] text-[10px] text-slate-400">Initializing Health Hub</p>
        </div>
      </motion.div>
    </div>

    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1, duration: 1 }}
      className="absolute bottom-12 text-[10px] font-bold text-slate-300 uppercase tracking-widest"
    >
      Secure Clinical Environment
    </motion.div>
  </div>
);

export const parseRouteFromHash = (rawHash: string): { page: string; tab?: string; action?: string } => {
  const clean = (rawHash || '').replace(/^[#/]+/, '').trim();
  if (!clean) return { page: 'home' };

  let base = clean;
  let queryString = '';

  if (clean.includes('?')) {
    const parts = clean.split('?');
    base = parts[0];
    queryString = parts.slice(1).join('?');
  } else if (clean.includes('&')) {
    const parts = clean.split('&');
    base = parts[0];
    queryString = parts.slice(1).join('&');
  }

  const searchParams = new URLSearchParams(queryString || (clean.startsWith('tab=') || clean.startsWith('action=') ? clean : ''));
  const tab = searchParams.get('tab') || undefined;
  const action = searchParams.get('action') || undefined;

  // Subpaths or direct tab aliases
  if (
    base === 'appointments' ||
    base === 'vitals' ||
    base === 'profile' ||
    base === 'vault' ||
    base === 'records' ||
    base === 'documents' ||
    base === 'doctor-notes' ||
    base === 'prescriptions' ||
    base === 'feedback' ||
    base.startsWith('tab=') ||
    base.startsWith('action=') ||
    base === 'dashboard'
  ) {
    const inferredTab = 
      tab ||
      (base === 'appointments' || clean.includes('tab=appointments') ? 'appointments' :
       base === 'doctor-notes' || base === 'prescriptions' || clean.includes('tab=doctor-notes') || clean.includes('tab=prescriptions') ? 'doctor-notes' :
       base === 'feedback' || clean.includes('tab=feedback') ? 'feedback' :
       base === 'vitals' || base === 'profile' || clean.includes('tab=vitals') || clean.includes('tab=profile') ? 'vitals' :
       base === 'vault' || base === 'records' || base === 'documents' || clean.includes('tab=vault') || clean.includes('tab=records') || clean.includes('tab=documents') ? 'vault' : undefined);
    
    const inferredAction = action || (clean.includes('action=upload') ? 'upload' : undefined);

    return {
      page: 'dashboard',
      tab: inferredTab,
      action: inferredAction
    };
  }

  const validPages = [
    'home', 'leadership', 'doctors', 'specialists', 'contact', 
    'login', 'register', 'dashboard', 'consultation', 
    'terms', 'privacy', 'ethics', 'faqs'
  ];

  const normalized = base.toLowerCase();
  if (normalized === 'specialists') return { page: 'doctors' };
  if (validPages.includes(normalized)) return { page: normalized };

  return { page: 'home' };
};

const AppContent: React.FC = () => {
  const { user, loading, bookAppointment, globalSettings } = useApp();
  const { showModal, closeModal, triggerInstall, hasNativePrompt } = usePwaInstall();
  // Initialize page from URL hash or default to home
  const [page, setPage] = useState(() => {
    const route = parseRouteFromHash(typeof window !== 'undefined' ? window.location.hash : '');
    return route.page || 'home';
  });

  // Synchronize state with URL hash
  useEffect(() => {
    const handleHashChange = () => {
      const route = parseRouteFromHash(window.location.hash);
      if (route.page) {
        setPage(route.page);
      }
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);

  const handleStartConsultation = (sessionId: string) => {
    setActiveSessionId(sessionId);
    setPage('consultation');
  };

  useEffect(() => {
    // Only update hash if it's different and we are NOT in an active session
    if (!activeSessionId) {
      const currentRoute = parseRouteFromHash(window.location.hash);
      if (currentRoute.page !== page) {
        window.location.hash = page;
      }
    }

    // Clean up transient cache-buster query parameters (like _t=...) from browser URL bar
    if (typeof window !== 'undefined' && window.location.search) {
      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.has('_t')) {
        searchParams.delete('_t');
        const remainingSearch = searchParams.toString();
        const cleanUrl = window.location.pathname + (remainingSearch ? `?${remainingSearch}` : '') + window.location.hash;
        window.history.replaceState(null, '', cleanUrl);
      }
    }

    // Scroll to top on page change
    window.scrollTo(0, 0);
  }, [page, activeSessionId]);

  // --- SEO Optimization Helper ---
  useEffect(() => {
    const siteName = globalSettings.platformName || "ROVITA";
    const titles: Record<string, string> = {
        'home': `Expert Healthcare Anywhere | ${siteName} Telemedicine`,
        'leadership': `Our Mission & Healthcare Leadership | ${siteName}`,
        'doctors': `Book Expert Specialists Online | ${siteName} Medical Network`,
        'contact': `Contact Patient Support | ${siteName} Digital Clinic`,
        'login': `Secure Patient Login | ${siteName} Portal`,
        'register': `Join Our Digital Health Network | ${siteName} Enrollment`,
        'dashboard': `My Health Portal | ${siteName}`,
        'faqs': `Patient FAQs & Support Center | ${siteName}`,
        'terms': `Terms of Service | ${siteName} Legal`,
        'privacy': `Privacy Policy & HIPAA Compliance | ${siteName}`,
    };

    const descriptions: Record<string, string> = {
        'home': `${siteName} offers premium telemedicine services, connecting you with board-certified doctors and specialists in minutes. Quality care from the comfort of home.`,
        'doctors': `Consult with world-class specialists across multiple medical fields. Instant booking and secure video consultations available 24/7.`,
        'register': `Create your ROVITA account to access global medical specialists, secure digital health records, and premium telemedicine services.`,
    };

    document.title = titles[page] || siteName;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
        metaDesc.setAttribute("content", descriptions[page] || descriptions['home']);
    }
  }, [page, globalSettings.platformName]);

  // Auto-redirect to dashboard when logged in
  useEffect(() => {
    if (user && (page === 'login' || page === 'register')) {
      setPage('dashboard');
    }
  }, [user, page]);

  return (
    <AnimatePresence mode="wait">
      {loading ? (
        <motion.div 
          key="loader"
          exit={{ opacity: 0, scale: 1.05 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
        >
          <LoadingScreen />
        </motion.div>
      ) : (
        <motion.div 
          key="content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="min-h-screen bg-white flex flex-col"
        >
          {/* Maintenance Check - Now includes a bypass for the Login page */}
          {globalSettings.maintenanceMode && user?.role !== 'admin' && page !== 'login' ? (
            <div className="h-screen flex flex-col items-center justify-center bg-slate-50 p-6 text-center">
              <div className="w-24 h-24 bg-white rounded-[2rem] shadow-2xl flex items-center justify-center text-amber-500 text-4xl mb-10 border border-amber-100">
                <i className="fa-solid fa-screwdriver-wrench"></i>
              </div>
              <h1 className="text-4xl font-black text-slate-900 mb-4 tracking-tight">System Undergoing Clinical Update</h1>
              <p className="text-slate-500 font-medium max-w-md mx-auto mb-10 leading-relaxed">
                We are currently performing scheduled maintenance to enhance your care experience. Please check back shortly.
              </p>
              <div className="flex flex-col gap-4">
                  <div className="text-[10px] font-black uppercase text-indigo-400 tracking-[0.4em]">ROVITA Virtual Health</div>
                  <button 
                      onClick={() => setPage('login')} 
                      className="text-xs font-bold text-slate-400 hover:text-indigo-600 transition-colors"
                  >
                      <i className="fa-solid fa-lock mr-2"></i> Administrative Access
                  </button>
              </div>
            </div>
          ) : (
            <>
              {page !== 'consultation' && <Header page={page} setPage={setPage} />}
              <main className="flex-grow">
                {(() => {
                  switch(page) {
                    case 'home': return <HomePage setPage={setPage} />;
                    case 'leadership': return <LeadershipPage />;
                    case 'doctors': return <SpecialistsPage onBook={bookAppointment} />;
                    case 'contact': return <ContactPage />;
                    case 'login': return <AuthPage key="login-page" initialType="login" setPage={setPage} />;
                    case 'register': return <AuthPage key="register-page" initialType="register" setPage={setPage} />;
                    case 'dashboard': return <Dashboard setPage={setPage} onStartConsultation={handleStartConsultation} />;
                    case 'consultation': 
                      if (!activeSessionId) {
                        return (
                          <div className="flex flex-col items-center justify-center py-32 px-6 text-center">
                            <h2 className="text-2xl font-black text-slate-900 mb-4">No Active Session</h2>
                            <p className="text-slate-500 font-medium mb-6">Please launch a consultation session from your dashboard.</p>
                            <Button onClick={() => setPage('dashboard')}>Return to Dashboard</Button>
                          </div>
                        );
                      }
                      return <ConsultationRoom appointmentId={activeSessionId} onEnd={() => { setActiveSessionId(null); setPage('dashboard'); }} />;
                    case 'terms': return <TermsAndServices />;
                    case 'privacy': return <PrivacyPolicy />;
                    case 'ethics': return <EthicsGuidelines />;
                    case 'faqs': return <FAQsPage />;
                    default: return <HomePage setPage={setPage} />;
                  }
                })()}
              </main>
              {page !== 'consultation' && <Footer setPage={setPage} />}
              {page !== 'consultation' && <GlobalLiveCallPopup onJoinRoom={handleStartConsultation} />}
              <CookieBanner />
              <ScrollToTop />
              <InstallPwaModal 
                isOpen={showModal} 
                onClose={closeModal} 
                onNativeInstall={triggerInstall}
                hasNativePrompt={hasNativePrompt}
              />
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const ScrollToTop: React.FC = () => {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const toggleVisible = () => {
            const scrolled = document.documentElement.scrollTop;
            if (scrolled > 500) {
                setVisible(true);
            } else {
                setVisible(false);
            }
        };
        window.addEventListener('scroll', toggleVisible);
        return () => window.removeEventListener('scroll', toggleVisible);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <button 
            onClick={scrollToTop}
            className={`fixed bottom-10 left-10 z-[600] w-16 h-16 bg-indigo-600 text-white rounded-2xl flex items-center justify-center shadow-2xl shadow-indigo-600/30 hover:bg-indigo-700 hover:-translate-y-2 transition-all duration-500 scale-0 ${visible ? 'scale-100' : 'scale-0'}`}
            title="Scroll to Top"
        >
            <i className="fa-solid fa-angles-up text-xl"></i>
        </button>
    );
};

const App: React.FC = () => <AppProvider><AppContent /></AppProvider>;
const root = createRoot(document.getElementById('root')!);
root.render(<App />);
